import * as THREE from 'three';
import { formatDistance } from './formatters';

export interface WaypointTarget {
  readonly id: string;
  readonly name: string;
  readonly getWorldPosition: (out: THREE.Vector3) => THREE.Vector3;
  /** When true, the marker renders in a compact size — used for
   *  close-range targets (e.g. a fleeing tutorial ship) where a full
   *  marker would obscure the actual silhouette. */
  readonly compact?: boolean;
}

export type WaypointSource = 'manual' | 'mission';

interface MarkerSlot {
  readonly source: WaypointSource;
  target: WaypointTarget | null;
  readonly markerEl: HTMLElement;
  readonly iconEl: HTMLElement;
  readonly arrowEl: HTMLElement;
  readonly labelEl: HTMLElement;
  readonly distanceEl: HTMLElement;
  readonly sourceEl: HTMLElement;
}

const _tmp = new THREE.Vector3();
const _ndc = new THREE.Vector3();

const MANUAL_ICON_SVG = `
<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <circle cx="16" cy="16" r="9" fill="none" stroke="#ffd389" stroke-width="2"/>
  <circle cx="16" cy="16" r="2.5" fill="#ffe38a"/>
  <line x1="16" y1="3" x2="16" y2="8" stroke="#ffd389" stroke-width="2"/>
  <line x1="16" y1="24" x2="16" y2="29" stroke="#ffd389" stroke-width="2"/>
  <line x1="3" y1="16" x2="8" y2="16" stroke="#ffd389" stroke-width="2"/>
  <line x1="24" y1="16" x2="29" y2="16" stroke="#ffd389" stroke-width="2"/>
</svg>`;

const MANUAL_ARROW_SVG = `
<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M16 4 L26 22 L16 18 L6 22 Z" fill="#ffe38a" stroke="#5a3f08" stroke-width="1.4"/>
</svg>`;

// Mission marker uses a diamond-with-pip glyph so the silhouette reads
// distinctly from the manual circle/cross even at small sizes. The
// stroke colors are overridden in CSS via .waypoint-marker.is-mission so
// the SVG is colour-neutral here.
const MISSION_ICON_SVG = `
<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M16 3 L29 16 L16 29 L3 16 Z" fill="none" stroke="#ffe8a8" stroke-width="2"/>
  <path d="M16 9 L23 16 L16 23 L9 16 Z" fill="rgba(255,232,168,0.32)" stroke="#ffe8a8" stroke-width="1.2"/>
  <circle cx="16" cy="16" r="2" fill="#ffe8a8"/>
</svg>`;

const MISSION_ARROW_SVG = `
<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M16 3 L27 22 L16 17 L5 22 Z" fill="rgba(255,232,168,0.85)" stroke="#5a3f08" stroke-width="1.4"/>
</svg>`;

/**
 * On-screen waypoint marker with two independent slots — "manual" (the
 * destination the player picks in the system map) and "mission" (driven
 * by the active mission step). Both project on top of the world; a
 * source pill labels each one when both are active so the player can
 * tell them apart.
 *
 * The HUD itself never decides waypoint state — callers set/clear each
 * slot independently. Slots can coexist; the mission slot is owned by
 * the mission system and cannot be cleared by the player.
 */
export class WaypointHUD {
  readonly element: HTMLElement;

  private readonly slots: { manual: MarkerSlot; mission: MarkerSlot };
  private playerPosProvider: (() => THREE.Vector3) | null = null;
  private camera: THREE.Camera | null = null;

  constructor() {
    this.element = document.createElement('div');
    this.element.className = 'waypoint-hud';

    this.slots = {
      manual: this.buildSlot('manual'),
      mission: this.buildSlot('mission')
    };
    this.element.append(this.slots.manual.markerEl, this.slots.mission.markerEl);
  }

  /** Sets the manual marker — the destination chosen by the player in
   *  the system map. The same camera + position provider are reused for
   *  both slots; whichever call set them last wins (in practice both
   *  callers pass identical references). */
  setTarget(
    target: WaypointTarget | null,
    camera: THREE.Camera | null,
    getPlayerPos: () => THREE.Vector3
  ): void {
    this.setSlot('manual', target, camera, getPlayerPos);
  }

  /** Sets the mission marker. Driven by the MissionManager via the
   *  GameDirector. Independent of the manual marker — the two coexist. */
  setMissionTarget(
    target: WaypointTarget | null,
    camera: THREE.Camera | null,
    getPlayerPos: () => THREE.Vector3
  ): void {
    this.setSlot('mission', target, camera, getPlayerPos);
  }

  private setSlot(
    source: WaypointSource,
    target: WaypointTarget | null,
    camera: THREE.Camera | null,
    getPlayerPos: () => THREE.Vector3
  ): void {
    const slot = this.slots[source];
    slot.target = target;
    if (target) {
      this.camera = camera;
      this.playerPosProvider = getPlayerPos;
      slot.labelEl.textContent = target.name;
      slot.markerEl.classList.add('is-active');
      slot.markerEl.classList.toggle('is-compact', target.compact === true);
    } else {
      slot.markerEl.classList.remove('is-active');
      slot.markerEl.classList.remove('is-compact');
    }
    this.refreshVisibility();
  }

  private refreshVisibility(): void {
    const manualActive = !!this.slots.manual.target;
    const missionActive = !!this.slots.mission.target;
    if (manualActive || missionActive) {
      this.element.classList.add('is-visible');
    } else {
      this.element.classList.remove('is-visible');
    }
    if (manualActive && missionActive) {
      this.element.classList.add('mission-and-manual');
    } else {
      this.element.classList.remove('mission-and-manual');
    }
  }

  /** Called once per frame. Projects every active slot independently. */
  update(): void {
    if (!this.camera || !this.playerPosProvider) {
      this.element.classList.remove('is-visible');
      return;
    }
    const manualActive = !!this.slots.manual.target;
    const missionActive = !!this.slots.mission.target;
    if (!manualActive && !missionActive) {
      this.element.classList.remove('is-visible');
      return;
    }
    this.element.classList.add('is-visible');

    if (manualActive) this.projectSlot(this.slots.manual);
    else this.slots.manual.markerEl.style.display = 'none';

    if (missionActive) this.projectSlot(this.slots.mission);
    else this.slots.mission.markerEl.style.display = 'none';
  }

  private projectSlot(slot: MarkerSlot): void {
    if (!slot.target || !this.camera || !this.playerPosProvider) return;
    slot.markerEl.style.display = '';

    slot.target.getWorldPosition(_tmp);
    const playerPos = this.playerPosProvider();
    const distance = _tmp.distanceTo(playerPos);

    _ndc.copy(_tmp).project(this.camera);

    const w = window.innerWidth;
    const h = window.innerHeight;
    const margin = 60;
    const halfW = w / 2;
    const halfH = h / 2;

    let screenX = (_ndc.x + 1) * 0.5 * w;
    let screenY = (1 - (_ndc.y + 1) * 0.5) * h;

    const inFront =
      _ndc.z < 1 && _ndc.x > -1.05 && _ndc.x < 1.05 && _ndc.y > -1.05 && _ndc.y < 1.05;

    if (inFront) {
      slot.markerEl.classList.remove('is-edge');
      slot.iconEl.style.display = '';
      slot.arrowEl.style.display = 'none';
    } else {
      slot.markerEl.classList.add('is-edge');
      slot.iconEl.style.display = 'none';
      slot.arrowEl.style.display = '';

      let dirX = _ndc.x;
      let dirY = -_ndc.y;
      if (_ndc.z >= 1) {
        dirX = -dirX;
        dirY = -dirY;
      }
      const len = Math.hypot(dirX, dirY) || 1;
      dirX /= len;
      dirY /= len;

      const edgeX = halfW + dirX * (halfW - margin);
      const edgeY = halfH + dirY * (halfH - margin);
      screenX = edgeX;
      screenY = edgeY;

      const angle = Math.atan2(dirY, dirX) + Math.PI / 2;
      slot.arrowEl.style.transform = `rotate(${angle}rad)`;
    }

    slot.markerEl.style.left = `${screenX}px`;
    slot.markerEl.style.top = `${screenY}px`;
    slot.distanceEl.textContent = formatDistance(distance);
  }

  private buildSlot(source: WaypointSource): MarkerSlot {
    const markerEl = document.createElement('div');
    markerEl.className = `waypoint-marker is-${source}`;

    const sourceEl = document.createElement('div');
    sourceEl.className = 'waypoint-marker-source';
    sourceEl.textContent = source === 'mission' ? 'Mission' : 'Marker';

    const iconEl = document.createElement('div');
    iconEl.className = 'waypoint-marker-icon';
    iconEl.innerHTML = source === 'mission' ? MISSION_ICON_SVG : MANUAL_ICON_SVG;

    const arrowEl = document.createElement('div');
    arrowEl.className = 'waypoint-marker-icon';
    arrowEl.innerHTML = source === 'mission' ? MISSION_ARROW_SVG : MANUAL_ARROW_SVG;
    arrowEl.style.display = 'none';

    const labelEl = document.createElement('div');
    labelEl.className = 'waypoint-marker-label';

    const distanceEl = document.createElement('div');
    distanceEl.className = 'waypoint-marker-distance';

    markerEl.append(sourceEl, iconEl, arrowEl, labelEl, distanceEl);

    return {
      source,
      target: null,
      markerEl,
      iconEl,
      arrowEl,
      labelEl,
      distanceEl,
      sourceEl
    };
  }
}
